/**
 * PURPOSE: Pestaña "Elegibles Rugby" de Administración General (FTR-0015), roles 1/4 por la frontera FTR-0012.
 * RESPONSIBILITIES: Empieza por elegir un evento (y su deporte Rugby); sin selección no muestra ninguna advertencia
 *   agregada. Para el evento elegido: un estado compacto ("Configuración completa" / "Configuración pendiente" con lo
 *   que falta, nunca la lista global de todos los evento-deportes), regla, plazo/cambios, el plazo de envío por fecha
 *   del fixture (RugbyFixtureDeadlinePanel.jsx) y el monitoreo de partidos (estado actual, plazo con advertencias,
 *   estado de cada lado, versión, cambios) con plazo absoluto por partido y la gestión de cada lado (edición
 *   administrativa, extensión, cambios extra, historial).
 * COLLABORATORS: rugbyLineupsService.js, rugbyLineups.utils.js, RugbyLineupConfigPanels.jsx,
 *   RugbyFixtureDeadlinePanel.jsx, RugbyLineupDialogs.jsx, RugbyLineupSidePanel.jsx, AdminCompanySection.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/CompanyRugbyLineupsTab.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiRefreshCw } from "react-icons/fi";

import {
  getRugbyEventSportOverview,
  listRugbyEventSports,
  retireRugbyMatchDeadline,
  saveRugbyMatchDeadline,
} from "../../services/rugbyLineupsService.js";
import { RugbyLineupPolicyPanel, RugbyLineupRulePanel, StatusNote } from "./RugbyLineupConfigPanels.jsx";
import RugbyFixtureDeadlinePanel from "./RugbyFixtureDeadlinePanel.jsx";
import { RugbyDeadlineDialog } from "./RugbyLineupDialogs.jsx";
import RugbyLineupSidePanel from "./RugbyLineupSidePanel.jsx";
import {
  DEADLINE_WARNING_LABELS,
  OFFICIAL_STATE_LABELS,
  describeRugbyDeadline,
  describeSubmissionConfiguration,
  getRugbyErrorMessage,
} from "./rugbyLineups.utils.js";

const BADGE_TONES = {
  emerald: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  rose: "bg-rose-100 text-rose-800",
  slate: "bg-slate-100 text-slate-700",
};
const Badge = ({ tone = "slate", children }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE_TONES[tone] || BADGE_TONES.slate}`}>{children}</span>
);

const pairKey = (item) => `${item.event.id}:${item.sport.id}`;

const SideCell = ({ side, onOpen }) => {
  if (!side.institution) return <span className="text-xs text-slate-400">Por definir</span>;
  const official = OFFICIAL_STATE_LABELS[side.submission?.officialState] || OFFICIAL_STATE_LABELS.NOT_SUBMITTED;
  const deadline = describeRugbyDeadline(side.deadline);
  return (
    <div className="space-y-1">
      <p className="text-sm font-semibold text-slate-800">{side.institution.abrev || side.institution.name}</p>
      <div className="flex flex-wrap gap-1">
        <Badge tone={official.tone}>{official.text}</Badge>
        {side.submission?.version > 0 && <Badge>v{side.submission.version}</Badge>}
        <Badge tone={side.submission?.allowance?.remainingChanges > 0 ? "slate" : "amber"}>{side.submission?.allowance?.remainingChanges ?? 0} cambios</Badge>
        {side.extension && <Badge tone="emerald">Extensión</Badge>}
      </div>
      {side.extension && <p className="text-[11px] text-slate-500">{deadline.text}</p>}
      <button type="button" onClick={onOpen} className="text-xs font-semibold text-blue-700 hover:underline">
        Gestionar
      </button>
    </div>
  );
};

const CompanyRugbyLineupsTab = ({ idcompany }) => {
  const [eventSports, setEventSports] = useState([]);
  const [selected, setSelected] = useState("");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState("");
  const [sidePanel, setSidePanel] = useState(null);
  const [deadlineMatch, setDeadlineMatch] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleError = useCallback((error, fallback) => {
    if (error?.response?.data?.code === "RUGBY_LINEUPS_DISABLED") {
      setUnavailable("Los elegibles Rugby no están habilitados en este entorno.");
      return;
    }
    toast.error(getRugbyErrorMessage(error, fallback));
  }, []);

  const loadList = useCallback(async () => {
    try {
      const items = await listRugbyEventSports();
      setEventSports(items);
      setSelected((current) => current || (items[0] ? pairKey(items[0]) : ""));
    } catch (error) {
      handleError(error, "No se pudieron cargar los evento-deportes Rugby.");
    }
  }, [handleError]);

  useEffect(() => {
    loadList();
  }, [idcompany, loadList]);

  const scope = selected ? { idevent: Number(selected.split(":")[0]), idsport: Number(selected.split(":")[1]) } : null;

  const loadOverview = useCallback(async () => {
    if (!selected) return;
    const [idevent, idsport] = selected.split(":").map(Number);
    setLoading(true);
    try {
      setOverview(await getRugbyEventSportOverview({ idevent, idsport }));
    } catch (error) {
      setOverview(null);
      handleError(error, "No se pudo cargar el evento-deporte.");
    } finally {
      setLoading(false);
    }
  }, [handleError, selected]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const refreshAll = () => {
    loadList();
    loadOverview();
  };

  const saveMatchDeadline = async (payload) => {
    setSaving(true);
    try {
      await saveRugbyMatchDeadline(deadlineMatch.id, payload);
      toast.success("Plazo del partido guardado.");
      setDeadlineMatch(null);
      loadOverview();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo guardar el plazo."));
    } finally {
      setSaving(false);
    }
  };

  const retireMatchDeadline = async (match) => {
    const reason = window.prompt("Se retirará el plazo propio del partido (vuelve al plazo del deporte). Motivo (opcional):", "");
    if (reason === null) return;
    try {
      await retireRugbyMatchDeadline(match.id, reason.trim());
      toast.success("Plazo del partido retirado.");
      loadOverview();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo retirar el plazo."));
    }
  };

  if (unavailable) return <StatusNote tone="amber">{unavailable}</StatusNote>;
  // El panel usa siempre los datos recargados del lado (extensión, estado) y no la copia con que se abrió.
  const panelMatch = sidePanel ? overview?.matches.find((match) => match.id === sidePanel.match.id) || sidePanel.match : null;
  const panelSide = sidePanel ? panelMatch?.sides.find((side) => side.side === sidePanel.side.side) || sidePanel.side : null;
  const configuration = overview ? describeSubmissionConfiguration(overview.configuration) : null;

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <label className="block max-w-xl flex-1 text-sm font-medium text-slate-700">
            Evento y deporte Rugby
            <select value={selected} onChange={(event) => setSelected(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal">
              {!eventSports.length && <option value="">Sin evento-deportes Rugby</option>}
              {eventSports.map((item) => (
                <option key={pairKey(item)} value={pairKey(item)}>
                  {item.event.name || `Evento ${item.event.id}`} · {item.sport.name || `Deporte ${item.sport.id}`}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={refreshAll} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <FiRefreshCw /> Recargar
          </button>
        </div>
      </section>

      {loading && !overview && <p className="text-sm text-slate-500">Cargando...</p>}
      {overview && scope && (
        <>
          <StatusNote tone={configuration.tone}>{configuration.text}</StatusNote>
          <div className="grid gap-5 xl:grid-cols-2">
            <RugbyLineupRulePanel scope={scope} ruleStatus={overview.rule} profiles={overview.profiles} onSaved={refreshAll} />
            <RugbyLineupPolicyPanel scope={scope} policy={overview.policy} onSaved={loadOverview} />
          </div>

          <RugbyFixtureDeadlinePanel scope={scope} matches={overview.matches} onSaved={loadOverview} />

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-bold text-slate-800">Partidos y envíos</h3>
            <p className="mb-3 text-xs text-slate-500">
              Hora del servidor: {new Date(overview.serverNow).toLocaleString("es-PE", { timeZone: "America/Lima" })} (Lima). La alineación solo es pública
              con el partido iniciado (En vivo), finalizado u oficializado. Los delegados editan solo con el partido Habilitado: si vuelve a Habilitado,
              siguen rigiendo el plazo y los cambios disponibles.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Partido</th>
                    <th className="px-3 py-2">Plazo</th>
                    <th className="px-3 py-2">Local</th>
                    <th className="px-3 py-2">Visita</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.matches.map((match) => {
                    const deadline = describeRugbyDeadline(match.deadline);
                    return (
                      <tr key={match.id} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-3">
                          <p className="font-semibold text-slate-800">#{match.id}{match.nroFecha !== null ? ` · Fecha ${match.nroFecha}` : ""}</p>
                          <p className="text-xs text-slate-500">{match.date || "Sin fecha"} {match.time || ""}</p>
                          <Badge tone={match.closed ? "slate" : match.state.id === 2 ? "rose" : "slate"}>{match.state.name || `Estado ${match.state.id}`}</Badge>
                        </td>
                        <td className="px-3 py-3">
                          <Badge tone={deadline.tone}>{deadline.text}</Badge>
                          {match.deadline.warnings.map((warning) => (
                            <p key={warning} className="mt-1 text-[11px] text-amber-700">{DEADLINE_WARNING_LABELS[warning] || warning}</p>
                          ))}
                          {!match.closed && (
                            <div className="mt-1 flex gap-2">
                              <button type="button" onClick={() => setDeadlineMatch(match)} className="text-xs font-semibold text-blue-700 hover:underline">
                                {match.override ? "Cambiar plazo del partido" : "Plazo del partido"}
                              </button>
                              {match.override && (
                                <button type="button" onClick={() => retireMatchDeadline(match)} className="text-xs font-semibold text-rose-700 hover:underline">
                                  Quitar
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        {match.sides.map((side) => (
                          <td key={side.side} className="px-3 py-3">
                            <SideCell side={side} onOpen={() => setSidePanel({ match, side })} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {!overview.matches.length && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-slate-500">Sin partidos en este evento-deporte.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {deadlineMatch && (
        <RugbyDeadlineDialog
          title={`Plazo del partido #${deadlineMatch.id}`}
          subtitle="Plazo absoluto (hora de Lima). Se mantiene aunque el partido se reprograme."
          initialLima={deadlineMatch.override?.closesAtLima || ""}
          reasonRequired={false}
          saving={saving}
          onCancel={() => setDeadlineMatch(null)}
          onSubmit={saveMatchDeadline}
        />
      )}
      {sidePanel && panelSide?.institution && (
        <RugbyLineupSidePanel match={panelMatch} side={panelSide} onClose={() => setSidePanel(null)} onChanged={loadOverview} />
      )}
    </div>
  );
};

export default CompanyRugbyLineupsTab;
